import type { TFunction } from 'i18next'
import { isSearchRequest, supportSessionErrorCode } from '@/lib/supportSessionErrors'

// i18next returns the raw key when missing. Treat that as "not translated"
// so callers can decide whether to use a fallback.
function lookup(t: TFunction, key: string): string | null {
  const result = t(key) as string
  return result && result !== key ? result : null
}

/**
 * Translate a Rails 422 error envelope into a localized toast string.
 *
 * Handles three response shapes:
 *   { error: { code, message, details } }   — preferred new shape
 *   { error: [ "msg1", "msg2" ] }           — legacy array
 *   { error: "msg" }                         — bare string
 *
 * When details contain Rails-style field error codes
 * ({ field: [{ error: 'blank' }] }) they are rendered as
 * "<translated field> <translated code>" instead of falling back to the
 * generic message.
 *
 * @param err     - The caught error (typically an axios error)
 * @param ns      - The i18next namespace to look up `errors.<code>`,
 *                  `errors.fields.*`, and `errors.codes.*` within
 * @param opts.fallback - Optional extra fallback string (returned when
 *                  everything else is unavailable)
 * @param opts.t  - Namespace `t` function (required for non-React callers)
 * @param opts.tc - Common-namespace `t` function (required for non-React callers)
 */
export function translateApiError(
  err: unknown,
  _ns: string,
  opts?: {
    fallback?: string
    t?: TFunction
    tc?: TFunction
  },
): string {
  const { fallback, t, tc } = opts ?? {}

  // tc is always required to look up the generic "something went wrong" key.
  // When called from the hook the closured tc is used; non-React callers must
  // pass opts.tc explicitly.
  const genericFallback =
    (tc ? (lookup(tc, 'errors.something_went_wrong') ?? null) : null) ??
    fallback ??
    'Something went wrong'

  // EP-160: a FlowRent support session's refusals read the same in every
  // namespace, so they come from common before any per-namespace code.
  const sessionCode = supportSessionErrorCode(err)
  if (sessionCode && tc) {
    const key = sessionCode === 'support_session_blocked' && isSearchRequest(err) ? 'search_blocked' : sessionCode
    return tc(`support_session.errors.${key}`)
  }

  const data = (err as { response?: { data?: unknown } }).response?.data
  const raw = (data as { error?: unknown } | undefined)?.error

  if (typeof raw === 'string') return raw
  if (Array.isArray(raw)) {
    return raw.filter((s): s is string => typeof s === 'string').join(', ') || genericFallback
  }
  if (!raw || typeof raw !== 'object') return genericFallback

  const obj = raw as {
    code?: string
    message?: string
    details?: Record<string, Array<{ error?: string }>>
  }

  // 1. If details give us per-field Rails error codes, render those translated.
  //    E.g. { description: [{ error: 'blank' }] } → "Description must not be blank"
  if (t && obj.details && typeof obj.details === 'object') {
    const parts: string[] = []
    for (const [field, errs] of Object.entries(obj.details)) {
      if (!Array.isArray(errs)) continue
      for (const e of errs) {
        const code = typeof e === 'object' && e && 'error' in e ? e.error : undefined
        if (!code) continue
        const fieldLabel = lookup(t, `errors.fields.${field}`) ?? field
        const codeLabel = lookup(t, `errors.codes.${code}`) ?? code
        parts.push(`${fieldLabel} ${codeLabel}`)
      }
    }
    if (parts.length > 0) return parts.join(', ')
  }

  // 2. Translate by top-level code within the given namespace.
  if (t && obj.code) {
    const translated = lookup(t, `errors.${obj.code}`)
    if (translated) return translated
  }

  // 3. Fall back to the server's human message, then the generic toast.
  return obj.message || genericFallback
}
